import { AlertTriangle, Check } from 'lucide-react';
import { usePlanLimits } from '@/hooks/usePlanLimits';

// Sentinels the plan catalog uses for "unlimited" (see routes/subscription.py _cap).
const isUnlimited = (value) => value === null || value === undefined || value >= 9999;

const RESOURCES = [
  { key: 'schools', label: 'Schools', planKey: 'max_schools' },
  { key: 'subs', label: 'Subs', planKey: 'max_subs' },
  { key: 'coAdmins', label: 'Co-admins', planKey: 'max_co_admins' },
  { key: 'postings', label: 'Postings this month', planKey: 'max_postings_per_month' },
];

// Current usage vs each candidate plan's limits, so an account can see before checkout whether
// anything would be archived at activation (KAN-136 FR-7). Usage comes from PlanLimitsContext.
export default function PlanUsageComparison() {
  const { plans, counts } = usePlanLimits();
  if (!plans?.length) return null;

  return (
    <section className="mb-8" data-testid="plan-usage-comparison">
      <h2 className="text-lg font-bold text-slate-900 font-['Manrope']">Your usage vs. each plan</h2>
      <p className="text-sm text-slate-600 mt-1 mb-4">
        If your account is over a plan&apos;s limits when it starts, the most recently created schools,
        Subs and co-admins beyond the limit are archived automatically — nothing is deleted. You can
        archive down first to choose what stays.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const overages = RESOURCES.filter(({ key, planKey }) => {
            const used = counts[key];
            return typeof used === 'number' && !isUnlimited(plan[planKey]) && used > plan[planKey];
          });
          return (
            <div
              key={plan.plan_id}
              className="rounded-lg border border-slate-200 bg-white p-4"
              data-testid={`plan-usage-${plan.plan_id}`}
            >
              <p className="font-medium text-slate-900">{plan.name}</p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {RESOURCES.map(({ key, label, planKey }) => {
                  const used = counts[key];
                  const limit = plan[planKey];
                  const over = typeof used === 'number' && !isUnlimited(limit) && used > limit;
                  return (
                    <li
                      key={key}
                      className={`flex items-start gap-2 ${over ? 'text-amber-700' : 'text-slate-600'}`}
                      data-testid={`plan-usage-${plan.plan_id}-${key}`}
                    >
                      {over
                        ? <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        : <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-emerald-600" />}
                      <span>
                        {label}: {typeof used === 'number' ? used : '—'} of {isUnlimited(limit) ? 'unlimited' : limit}
                        {over && ` — ${plan.name} allows ${limit}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {overages.length > 0 && (
                <p className="mt-3 text-xs text-amber-700" data-testid={`plan-usage-${plan.plan_id}-warning`}>
                  Excess would be archived when this plan starts.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
